import { useSelector } from 'react-redux';

import { RootState } from './store';

interface Selector<T> {
  raw: (state: RootState) => T;
  useHook: () => T;
}

function createSelector<T>(
  f: (state: RootState) => T,
  equalityFn?: (left: T, right: T) => boolean,
): Selector<T> {
  return {
    raw: f,
    useHook() {
      return useSelector(f, equalityFn);
    },
  };
}

/* `preference` only selectors */
export const selectPath = createSelector((s: RootState) => {
  return s.preference.preferences.path;
});

export const selectHierarchy = createSelector((state: RootState) => {
  return state.preference.hierarchy;
});

export const selectPreferences = createSelector((state: RootState) => {
  return state.preference.preferences;
});

export const selectIsPreferenceLoading = createSelector((state: RootState) => {
  return state.preference.isLoading;
});

export const selectCurrentAsset = createSelector((state: RootState) => {
  return state.preference.preferences?.selectedAsset;
});

export const selectHasPreferences = createSelector((state: RootState) => {
  return state.preference.preferences?.selectedAsset !== undefined;
});

export const selectPreferencesError = createSelector((state: RootState) => {
  return state.preference.error;
});

export const selectCurrentSiteCode = createSelector((state: RootState) => {
  return state.preference.preferences?.site?.siteCode;
});

export const selectAllSites = createSelector((state: RootState) => {
  return state.preference.hierarchy?.sites;
});

export const selectAllBusinessTeams = createSelector((state: RootState) => {
  return state.preference.preferences?.site?.businessTeams ?? {};
});

export const selectAllConsoles = createSelector((state: RootState) => {
  return state.preference.preferences?.businessTeam?.consoles ?? {};
});

export const selectAllPosts = createSelector((state: RootState) => {
  return state.preference.preferences?.console?.posts ?? {};
});

export const selectPreferenceModal = createSelector((state: RootState) => {
  return state.preference.modal;
});

/* `shiftLogs` only selectors */
export const selectReportTemplates = createSelector((state: RootState) => {
  return state.shiftLogs.reportTemplates;
});

export const selectIsShiftLogsLoading = createSelector((state: RootState) => {
  return state.shiftLogs.isLoading;
});

export const selectShiftLogError = createSelector((state: RootState) => {
  return state.shiftLogs.errorMessage;
});

export const selectShiftLogModal = createSelector((state: RootState) => {
  return state.shiftLogs.modal;
});

export const selectTemplateSelected = createSelector((state: RootState) => {
  return state.shiftLogs.selectedTemplate;
});

export const selectShiftLogDetails = createSelector((state: RootState) => {
  return state.shiftLogs.shiftLogDetails;
});

export const selectAllUserShiftLogs = createSelector((state: RootState) => {
  return state.shiftLogs.shiftLogs;
});

export const selectIsLogDetailLoading = createSelector((state: RootState) => {
  return state.shiftLogs.logDetailIsLoading;
});

export const selectLogDetailError = createSelector((state: RootState) => {
  return state.shiftLogs.logDetailErrorMessage;
});

export const selectCurrentShiftLog = createSelector((state: RootState) => {
  return [...state.shiftLogs.teamShiftLogs, ...state.shiftLogs.shiftLogs].find((shiftLog) => {
    return shiftLog.shiftSummaryID === state.shiftLogs.shiftLogDetails?.shiftSummaryID;
  });
});

export const selectAllTeamShiftLogs = createSelector((state: RootState) => {
  return state.shiftLogs.teamShiftLogs;
});

export const selectSnippets = createSelector((state: RootState) => {
  return state.shiftLogs.shiftLogDetails?.snippets;
});

/* `user` only selectors */
export const selectUser = createSelector((state: RootState) => {
  return state.user.user;
});

export const selectHasEditSomeSnippet = createSelector((state: RootState) => {
  return state.editing.formState.dirty;
});

export const selectEditedSnippets = createSelector((state: RootState) => {
  return state.editing.editedSnippet;
});

export const selectFormState = createSelector((state: RootState) => {
  return state.editing.formState;
});

export const selectEditingMode = createSelector((state: RootState) => {
  return state.editing.mode;
});
